// Video optimization utility to prevent video loading on mobile
class VideoOptimizer {
  constructor() {
    this.isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    this.isSlowConnection = navigator.connection && (
      navigator.connection.effectiveType === 'slow-2g' || 
      navigator.connection.effectiveType === '2g' ||
      navigator.connection.downlink < 1
    );
    this.isLowEndDevice = this.isMobile && (
      (navigator.deviceMemory && navigator.deviceMemory < 4) ||
      (navigator.hardwareConcurrency && navigator.hardwareConcurrency < 4)
    );
    
    this.init();
  }

  init() {
    if (this.shouldOptimize()) {
      this.preventVideoLoading();
      this.optimizeExistingVideos();
      this.setupVideoObserver();
    }
  }

  shouldOptimize() {
    return this.isMobile || this.isSlowConnection || this.isLowEndDevice;
  }

  preventVideoLoading() {
    // Override video element creation to prevent loading
    const originalCreateElement = document.createElement.bind(document);
    
    document.createElement = function(tagName) {
      const element = originalCreateElement(tagName);
      
      if (tagName.toLowerCase() === 'video' && this.shouldOptimize()) {
        // Prevent video loading on mobile
        element.setAttribute('data-mobile-optimized', 'true');
        element.setAttribute('preload', 'none');
        element.setAttribute('data-src', element.src || '');
        element.removeAttribute('src');
        
        // Add event listeners to prevent loading
        element.addEventListener('loadstart', this.preventVideoLoad.bind(this), true);
        element.addEventListener('loadedmetadata', this.preventVideoLoad.bind(this), true);
      }
      
      return element;
    }.bind(this);

    // Override HTMLVideoElement prototype methods
    if (HTMLVideoElement && HTMLVideoElement.prototype) {
      const originalLoad = HTMLVideoElement.prototype.load;
      HTMLVideoElement.prototype.load = function() {
        if (this.hasAttribute('data-mobile-optimized')) {
          console.log('Video loading prevented on mobile for performance');
          return;
        }
        return originalLoad.call(this);
      };
    }
  }

  preventVideoLoad(event) {
    event.preventDefault();
    event.stopPropagation();
    console.log('Video loading prevented on mobile for performance');
    return false;
  }

  optimizeExistingVideos() {
    const videos = document.querySelectorAll('video');
    videos.forEach(video => this.optimizeVideo(video));
  }

  optimizeVideo(video) {
    if (!this.shouldOptimize()) return;

    // Store original source
    const originalSrc = video.src || video.querySelector('source')?.src;
    if (originalSrc) {
      video.setAttribute('data-original-src', originalSrc);
      video.setAttribute('data-mobile-optimized', 'true');
    }

    // Remove source to prevent loading
    video.removeAttribute('src');
    const sources = video.querySelectorAll('source');
    sources.forEach(source => {
      source.setAttribute('data-original-src', source.src);
      source.removeAttribute('src');
    });

    // Set preload to none
    video.setAttribute('preload', 'none');
    
    // Add mobile-optimized class
    video.classList.add('mobile-video-optimized');
    
    // Create placeholder if no poster
    if (!video.poster) {
      this.createVideoPlaceholder(video);
    }

    console.log('Video optimized for mobile:', video);
  }

  createVideoPlaceholder(video) {
    const placeholder = document.createElement('div');
    placeholder.className = 'video-mobile-placeholder';
    placeholder.innerHTML = `
      <div style="
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: #000;
        display: flex;
        align-items: center;
        justify-content: center;
        color: white;
        font-family: Arial, sans-serif;
        text-align: center;
        border-radius: 8px;
      ">
        <div>
          <div style="font-size: 48px; margin-bottom: 10px;">🎥</div>
          <div style="font-size: 14px;">Video content</div>
          <div style="font-size: 12px; opacity: 0.7; margin-top: 5px;">
            ${this.isSlowConnection ? 'Optimized for slow connection' : 'Mobile optimized'}
          </div>
        </div>
      </div>
    `;
    
    placeholder.style.cssText = `
      position: relative;
      width: 100%;
      height: 100%;
      min-height: 200px;
    `;
    
    // Insert placeholder before video
    video.parentNode.insertBefore(placeholder, video);
    video.style.display = 'none';
  }

  setupVideoObserver() {
    if (!window.MutationObserver) return;

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === Node.ELEMENT_NODE) {
            if (node.tagName === 'VIDEO') {
              this.optimizeVideo(node);
            } else {
              const videos = node.querySelectorAll ? node.querySelectorAll('video') : [];
              videos.forEach(video => this.optimizeVideo(video));
            }
          }
        });
      });
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  // Method to manually enable video loading (for user interaction)
  enableVideo(video) {
    if (!video.hasAttribute('data-mobile-optimized')) return;

    const originalSrc = video.getAttribute('data-original-src');
    if (originalSrc) {
      video.src = originalSrc;
      video.removeAttribute('data-mobile-optimized');
      video.classList.remove('mobile-video-optimized');
      
      // Restore sources
      const sources = video.querySelectorAll('source');
      sources.forEach(source => {
        const originalSrc = source.getAttribute('data-original-src');
        if (originalSrc) {
          source.src = originalSrc;
          source.removeAttribute('data-original-src');
        }
      });
      
      console.log('Video loading enabled for:', video);
    }
  }

  // Method to check if video is optimized
  isVideoOptimized(video) {
    return video.hasAttribute('data-mobile-optimized');
  }

  // Get optimization stats
  getStats() {
    const optimizedVideos = document.querySelectorAll('[data-mobile-optimized="true"]');
    const totalVideos = document.querySelectorAll('video').length;
    
    return {
      totalVideos,
      optimizedVideos: optimizedVideos.length,
      optimizationEnabled: this.shouldOptimize(),
      isMobile: this.isMobile,
      isSlowConnection: this.isSlowConnection,
      isLowEndDevice: this.isLowEndDevice
    };
  }
}

// Create singleton instance
const videoOptimizer = new VideoOptimizer();

export default videoOptimizer;
